import type { QueryClient } from '@tanstack/react-query';
import { notFound, redirect } from '@tanstack/react-router';

import { ForbiddenError } from '@/features/shell';
import { authGate, isApiError } from '@/shared/api';
import { can, sessionQuery, type PermissionCode } from '@/shared/auth';

/** Ensures a session exists; otherwise sends the user to sign in and back. */
export async function requireSession(queryClient: QueryClient, href: string) {
  try {
    const session = await queryClient.query({ ...sessionQuery, staleTime: 'static' });
    authGate.enable();
    return session;
  } catch (e) {
    if (isApiError(e) && e.isUnauthorized) {
      throw redirect({ to: '/login', search: { redirect: href } });
    }
    throw e;
  }
}

/** Route-level permission check. Renders the in-shell "No access" view; never redirects. */
export async function requirePermission(
  queryClient: QueryClient,
  permission: PermissionCode | PermissionCode[],
  area: string,
) {
  const session = await queryClient.query({ ...sessionQuery, staleTime: 'static' });
  const codes = Array.isArray(permission) ? permission : [permission];
  if (!codes.some((c) => can(session, c))) throw new ForbiddenError(codes.join(' or '), area);
}

/** Loads a record for a detail route; an API 404 becomes the route's in-shell "not found" view. */
export async function ensureRecord<T>(load: Promise<T>): Promise<T> {
  try {
    return await load;
  } catch (e) {
    if (isApiError(e) && e.isNotFound) throw notFound();
    throw e;
  }
}
